/**
 * Internal Graph Component
 *
 * Renders a contained sub-graph inside an expanded GROUP node.
 * Uses ReactFlow for visualization with simplified controls.
 */

import React, { useMemo, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Position,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
  Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  LogicalNode,
  ChildEdge,
  isGroupNode,
  isEndNode,
  getNodeTypeColor,
  getNodeTypeIcon,
} from '../types';
import { formatCount } from '../../../utils/plural';

interface InternalGraphProps {
  children: LogicalNode[];
  childEdges?: ChildEdge[] | null;
  onFindingClick?: (findingId: string) => void;
  onNodeClick?: (nodeId: string) => void;
}

/**
 * InternalNode - Compact node for use within InternalGraph
 * Supports expansion to show nested children or finding links
 */
const InternalNodeComponent: React.FC<{
  data: { node: LogicalNode; onFindingClick?: (findingId: string) => void };
}> = ({ data }) => {
  const { node, onFindingClick } = data;
  const [isExpanded, setIsExpanded] = useState(false);

  const color = getNodeTypeColor(node.node_type);
  const icon = getNodeTypeIcon(node.node_type);
  const isGroup = isGroupNode(node);
  const isEnd = isEndNode(node);
  const hasExpandableContent = isGroup || isEnd;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={`px-2.5 py-2 rounded-md border-2 bg-surface shadow-sm transition-all relative ${
        isExpanded ? 'min-w-[200px] max-w-[280px]' : 'min-w-[140px] max-w-[200px]'
      }`}
      style={{ borderColor: color }}
    >
      {/* Input Handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-2 h-2 !bg-fg-tertiary border border-white"
      />
      {/* Output Handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2 h-2 !bg-fg-tertiary border border-white"
      />
      {/* Header */}
      <div className="flex items-center gap-2">
        {/* Expand button for nodes with children or findings */}
        {hasExpandableContent && (
          <button
            onClick={handleExpandClick}
            className="w-4 h-4 flex items-center justify-center rounded hover:bg-bg-warm transition-colors text-fg-tertiary"
          >
            <svg
              className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
        <span
          className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          {icon}
        </span>
        <span className="text-xs font-medium text-fg truncate flex-1">
          {node.label || node.node_id}
        </span>
      </div>

      {/* Content preview (always shown) */}
      {node.content && (
        <p className={`mt-1 text-[10px] text-fg-tertiary ${isExpanded ? '' : 'line-clamp-2'}`}>
          {node.content}
        </p>
      )}

      {/* Collapsed state: show count badges */}
      {!isExpanded && (
        <>
          {isGroup && node.children && (
            <button
              onClick={handleExpandClick}
              className="mt-1.5 text-[10px] text-node-definition font-medium hover:text-node-definition transition-colors"
            >
              → {formatCount(node.children.length, { one: 'nested item', other: 'nested items' })}
            </button>
          )}
          {isEnd && node.finding_ids && (
            <button
              onClick={handleExpandClick}
              className="mt-1.5 text-[10px] text-success font-medium hover:text-success transition-colors"
            >
              → {formatCount(node.finding_ids.length, { one: 'finding', other: 'findings' })}
            </button>
          )}
        </>
      )}

      {/* Expanded state: show full content */}
      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-divider">
          {/* Nested children for GROUP nodes */}
          {isGroup && node.children && (
            <div className="space-y-1.5">
              <div className="text-[10px] text-node-definition font-medium mb-1">
                Nested items:
              </div>
              {node.children.map((child) => (
                <div
                  key={child.node_id}
                  className="pl-2 border-l-2 border-node-definition text-[10px] text-fg-secondary"
                >
                  <span className="font-medium">{child.label || child.node_id}</span>
                  {child.content && (
                    <p className="text-fg-tertiary line-clamp-1">{child.content}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Finding links for END nodes */}
          {isEnd && node.finding_ids && (
            <div>
              <div className="text-[10px] text-success font-medium mb-1.5">
                Linked findings:
              </div>
              <div className="flex flex-wrap gap-1">
                {node.finding_ids.map((findingId) => (
                  <button
                    key={findingId}
                    className="px-1.5 py-0.5 text-[10px] bg-success-bg text-success rounded hover:bg-success-bg transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFindingClick?.(findingId);
                    }}
                  >
                    {findingId}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Define nodeTypes at module level for stable reference (React Flow requirement)
const nodeTypes = {
  internalNode: InternalNodeComponent,
};

/**
 * Layout children in a hierarchical top-to-bottom flow
 */
function layoutChildren(
  children: LogicalNode[],
  childEdges?: ChildEdge[] | null,
  onFindingClick?: (findingId: string) => void
): Node[] {
  const nodeWidth = 180;
  const nodeHeight = 70;
  const horizontalGap = 40;
  const verticalGap = 50;

  // Normalize childEdges to empty array if null/undefined
  const edges = childEdges ?? [];

  // Store onFindingClick for passing to nodes
  const findingClickHandler = onFindingClick;

  // Build adjacency for topological sort
  const inDegree: Record<string, number> = {};
  const outgoing: Record<string, string[]> = {};

  children.forEach((child) => {
    inDegree[child.node_id] = 0;
    outgoing[child.node_id] = [];
  });

  edges.forEach((edge) => {
    if (inDegree[edge.target_id] !== undefined) {
      inDegree[edge.target_id]++;
    }
    if (outgoing[edge.source_id]) {
      outgoing[edge.source_id].push(edge.target_id);
    }
  });

  // Topological sort to determine levels
  const nodeLevels: Record<string, number> = {};
  const queue: string[] = [];

  Object.keys(inDegree).forEach((nodeId) => {
    if (inDegree[nodeId] === 0) {
      queue.push(nodeId);
    }
  });

  let level = 0;
  while (queue.length > 0) {
    const levelSize = queue.length;
    for (let i = 0; i < levelSize; i++) {
      const nodeId = queue.shift()!;
      nodeLevels[nodeId] = level;

      if (outgoing[nodeId]) {
        outgoing[nodeId].forEach((targetId) => {
          inDegree[targetId]--;
          if (inDegree[targetId] === 0) {
            queue.push(targetId);
          }
        });
      }
    }
    level++;
  }

  // Assign default level to any unvisited nodes
  children.forEach((child) => {
    if (nodeLevels[child.node_id] === undefined) {
      nodeLevels[child.node_id] = 0;
    }
  });

  // Group nodes by level
  const levelGroups: Record<number, LogicalNode[]> = {};
  children.forEach((child) => {
    const lvl = nodeLevels[child.node_id];
    if (!levelGroups[lvl]) levelGroups[lvl] = [];
    levelGroups[lvl].push(child);
  });

  // Position nodes
  const nodes: Node[] = [];
  const levels = Object.keys(levelGroups).map(Number).sort((a, b) => a - b);

  levels.forEach((lvl) => {
    const nodesInLevel = levelGroups[lvl];
    const totalWidth = nodesInLevel.length * (nodeWidth + horizontalGap) - horizontalGap;
    const startX = -totalWidth / 2 + nodeWidth / 2;

    nodesInLevel.forEach((child, index) => {
      nodes.push({
        id: child.node_id,
        position: {
          x: startX + index * (nodeWidth + horizontalGap),
          y: lvl * (nodeHeight + verticalGap) + 20,
        },
        data: { node: child, onFindingClick: findingClickHandler },
        type: 'internalNode',
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      });
    });
  });

  return nodes;
}

/**
 * Convert child_edges to ReactFlow edges
 * If no explicit edges provided, creates sequential edges between nodes (step 1 → step 2 → ...)
 */
function createEdges(childEdges?: ChildEdge[] | null, children?: LogicalNode[]): Edge[] {
  let edges = childEdges ?? [];

  // If no explicit edges but we have multiple children, create sequential edges
  if (edges.length === 0 && children && children.length > 1) {
    edges = children.slice(0, -1).map((child, index) => ({
      source_id: child.node_id,
      target_id: children[index + 1].node_id,
      label: undefined,
    }));
  }

  return edges.map((edge, index) => ({
    id: `internal-edge-${index}`,
    source: edge.source_id,
    target: edge.target_id,
    label: edge.label,
    animated: false,
    style: { stroke: '#94a3b8', strokeWidth: 1.5 },
    labelStyle: { fontSize: 9, fill: '#64748b' },
    labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.9 },
    labelBgPadding: [4, 2] as [number, number],
    labelBgBorderRadius: 4,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: '#94a3b8',
      width: 15,
      height: 15,
    },
  }));
}

/**
 * Inner component that uses React Flow hooks
 * Must be wrapped in ReactFlowProvider
 */
const InternalGraphInner: React.FC<InternalGraphProps> = ({
  children,
  childEdges,
  onFindingClick,
  onNodeClick,
}) => {
  // Layout nodes and create edges - pass onFindingClick to be included in node data
  const initialNodes = useMemo(
    () => layoutChildren(children, childEdges, onFindingClick),
    [children, childEdges, onFindingClick]
  );
  const initialEdges = useMemo(() => createEdges(childEdges, children), [childEdges, children]);

  const [nodes] = useNodesState(initialNodes);
  const [edges] = useEdgesState(initialEdges);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodeClick={(_event, node) => onNodeClick?.(node.id)}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      minZoom={0.5}
      maxZoom={1.5}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={true}
      panOnDrag={true}
      zoomOnScroll={false}
      zoomOnPinch={false}
      preventScrolling={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#e2e8f0" />
    </ReactFlow>
  );
};

/**
 * InternalGraph - Renders children of a GROUP node as a contained sub-graph
 * Wraps inner component with ReactFlowProvider for nested ReactFlow instance
 */
const InternalGraph: React.FC<InternalGraphProps> = (props) => {
  if (props.children.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-fg-tertiary">
        No children
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <InternalGraphInner {...props} />
    </ReactFlowProvider>
  );
};

export default InternalGraph;
